import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import orderService from '../../services/orderService';
import './OrderDetailPage.css';

/**
 * OrderDetailPage - Read-only view of order details
 * NOTE: CMS Backend is read-only. Order modifications must be done via Main Backend.
 */
function OrderDetailPage() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await orderService.getOrder(id);
        // Handle ApiResponse wrapper - data could be in response.data.data or response.data
        const orderData = response.data?.data || response.data;
        setOrder(orderData);

        // Use embedded timeline if available, otherwise fetch separately
        if (orderData?.timeline && Array.isArray(orderData.timeline)) {
          setTimeline(orderData.timeline);
        } else {
          // Fetch timeline separately
          fetchTimeline();
        }
      } catch (err) {
        setError('Failed to load order');
        console.error('Error fetching order:', err);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchTimeline = async () => {
      try {
        const response = await orderService.getOrderTimeline(id);
        // Handle ApiResponse wrapper
        const timelineData = response.data?.data || response.data;
        if (Array.isArray(timelineData)) {
          setTimeline(timelineData);
        }
      } catch (err) {
        console.error('Error fetching timeline:', err);
        // Timeline fetch failure is not critical, don't set error
      }
    };

    fetchOrder();
  }, [id]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pending', className: 'badge-warning' },
      processing: { label: 'Processing', className: 'badge-info' },
      shipped: { label: 'Shipped', className: 'badge-purple' },
      delivered: { label: 'Delivered', className: 'badge-success' },
      cancelled: { label: 'Cancelled', className: 'badge-danger' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <span className={`badge ${config.className}`}>{config.label}</span>;
  };

  const getPaymentBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pending', className: 'badge-warning' },
      paid: { label: 'Paid', className: 'badge-success' },
      failed: { label: 'Failed', className: 'badge-danger' },
      refunded: { label: 'Refunded', className: 'badge-secondary' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <span className={`badge ${config.className}`}>{config.label}</span>;
  };

  if (isLoading) {
    return (
      <div className="order-detail-page">
        <div className="page-loading">
          <div className="loading-spinner"></div>
          <span>Loading order...</span>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="order-detail-page">
        <div className="page-error">
          <h2>Order Not Found</h2>
          <p>{error || 'The order you are looking for does not exist.'}</p>
          <Link to="/orders" className="btn btn-primary">
            Back to Orders
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="order-detail-page">
      <div className="page-header">
        <div className="page-header-content">
          <div className="breadcrumb">
            <Link to="/orders">Orders</Link>
            <span>/</span>
            <span>{order.orderNumber}</span>
          </div>
          <div className="header-title-row">
            <h1>{order.orderNumber}</h1>
            {getStatusBadge(order.status)}
            {getPaymentBadge(order.paymentStatus)}
          </div>
          <p>Placed on {formatDate(order.createdAt)}</p>
        </div>
      </div>

      <div className="detail-grid">
        {/* Main Content */}
        <div className="detail-main">
          {/* Order Items */}
          <div className="detail-section">
            <h3>Order Items</h3>
            <div className="order-items">
              {order.items.map((item) => (
                <div key={item.id} className="order-item">
                  <div className="item-image">
                    {item.image ? (
                      <img src={item.image} alt={item.name} className="product-image" />
                    ) : (
                      <div className="image-placeholder">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="item-details">
                    <h4>{item.name}</h4>
                    <p className="item-sku">SKU: {item.sku}</p>
                  </div>
                  <div className="item-quantity">
                    <span className="label">Qty</span>
                    <span className="value">{item.quantity}</span>
                  </div>
                  <div className="item-price">
                    <span className="unit-price">{formatPrice(item.price)} each</span>
                    <span className="total-price">{formatPrice(item.total)}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Totals */}
            <div className="order-totals">
              <div className="total-row">
                <span>Subtotal</span>
                <span>{formatPrice(order.subtotal)}</span>
              </div>
              <div className="total-row">
                <span>Shipping</span>
                <span>{order.shipping === 0 ? 'Free' : formatPrice(order.shipping)}</span>
              </div>
              {order.discount > 0 && (
                <div className="total-row discount">
                  <span>Discount</span>
                  <span>-{formatPrice(order.discount)}</span>
                </div>
              )}
              <div className="total-row">
                <span>Tax</span>
                <span>{formatPrice(order.tax)}</span>
              </div>
              <div className="total-row grand-total">
                <span>Total</span>
                <span>{formatPrice(order.total)}</span>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="detail-section">
            <h3>Order Timeline</h3>
            {timeline.length > 0 ? (
              <div className="timeline">
                {timeline.map((event, index) => (
                  <div key={event.id || index} className={`timeline-item ${index === timeline.length - 1 ? 'latest' : ''}`}>
                    <div className="timeline-marker">
                      <div className="marker-dot"></div>
                      {index < timeline.length - 1 && <div className="marker-line"></div>}
                    </div>
                    <div className="timeline-content">
                      <h4>{event.event}</h4>
                      <p>{event.description}</p>
                      <span className="timeline-date">{formatDate(event.timestamp)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-timeline">No timeline events available.</p>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="detail-sidebar">
          {/* Customer Info */}
          <div className="detail-section">
            <h3>Customer</h3>
            <div className="customer-card">
              <div className="customer-avatar">
                {order.customer?.firstName?.charAt(0) || ''}{order.customer?.lastName?.charAt(0) || ''}
              </div>
              <div className="customer-details">
                <h4>{order.customer?.firstName} {order.customer?.lastName}</h4>
                <p>
                  <a href={`mailto:${order.customer?.email}`}>{order.customer?.email}</a>
                </p>
                {order.customer?.phone && <p>{order.customer.phone}</p>}
              </div>
            </div>
            {order.customer?.id && (
              <Link to={`/customers/${order.customer.id}`} className="btn btn-text btn-full">
                View Customer Profile
              </Link>
            )}
          </div>

          {/* Shipping Address */}
          {order.shippingAddress ? (
            <div className="detail-section">
              <h3>Shipping Address</h3>
              <div className="address">
                <p>{order.shippingAddress.line1}</p>
                {order.shippingAddress.line2 && <p>{order.shippingAddress.line2}</p>}
                <p>{order.shippingAddress.city}</p>
                <p>{order.shippingAddress.postcode}</p>
                <p>{order.shippingAddress.country}</p>
              </div>
            </div>
          ) : (
            <div className="detail-section">
              <h3>Shipping Address</h3>
              <p className="no-address">No shipping address provided.</p>
            </div>
          )}

          {/* Billing Address */}
          {order.billingAddress ? (
            <div className="detail-section">
              <h3>Billing Address</h3>
              <div className="address">
                <p>{order.billingAddress.line1}</p>
                {order.billingAddress.line2 && <p>{order.billingAddress.line2}</p>}
                <p>{order.billingAddress.city}</p>
                <p>{order.billingAddress.postcode}</p>
                <p>{order.billingAddress.country}</p>
              </div>
            </div>
          ) : (
            <div className="detail-section">
              <h3>Billing Address</h3>
              <p className="no-address">No billing address provided.</p>
            </div>
          )}

          {/* Order Notes */}
          {order.notes && (
            <div className="detail-section">
              <h3>Order Notes</h3>
              <p className="order-notes">{order.notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default OrderDetailPage;
